/**
 * Internationalisation.
 *
 * Wired in from the very first string rather than retrofitted. Retrofitting i18n
 * means walking every component in the app looking for literals, and the ones
 * that get missed are always in error paths nobody reads until they matter.
 *
 * English is the default and the fallback, so `ta.json` can be filled in
 * progressively — a missing Tamil key renders the English string rather than a
 * raw key name.
 */

import i18next from 'i18next'
import { initReactI18next } from 'react-i18next'
import en from './en.json'
import ta from './ta.json'

export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'ta', label: 'தமிழ்' },
] as const

export type LanguageCode = (typeof SUPPORTED_LANGUAGES)[number]['code']

const STORAGE_KEY = 'ifl.language'

function detectLanguage(): LanguageCode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored && SUPPORTED_LANGUAGES.some((l) => l.code === stored)) {
      return stored as LanguageCode
    }
  } catch {
    // Private browsing can throw on localStorage; fall through to the default.
  }
  return navigator.language?.startsWith('ta') ? 'ta' : 'en'
}

void i18next.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    ta: { translation: ta },
  },
  lng: detectLanguage(),
  fallbackLng: 'en',
  interpolation: {
    // React already escapes interpolated values.
    escapeValue: false,
  },
  returnNull: false,
})

export function setLanguage(code: LanguageCode): void {
  void i18next.changeLanguage(code)
  document.documentElement.lang = code
  try {
    localStorage.setItem(STORAGE_KEY, code)
  } catch {
    // Not being able to remember the choice is not worth failing over.
  }
}

document.documentElement.lang = i18next.language

export default i18next
